// 스킬 타임라인 vfx 큐 이름 → 모듈 조합 + 파라미터 매핑 표(PRD 3A.3, COMBAT-FEEL 2.10).
// 정본 큐 이름은 src/shared/skills/<hero>.ts의 cues[]다(채널 'vfx'). 이 표는 그 이름마다 어떤 모듈을 어떤 색·크기·길이로 띄울지 적는다.
// live: true인 큐만 게임이 새 모듈로 그린다. live: false는 기록뿐이다(프리뷰에서 force로만 재생).
// c006: 스킬 5 천공 올려베기만 live. c012: Kael 스킬 5종의 모든 vfx 큐를 live로 연결했다(선딜·판정·마무리/착지).
// 기존 연출(slashes·skillFx·treeFx) 중 이 표가 대신하는 조각은 CombatFx.skillsByModules가 끈다(두 번 그리지 않는다):
//   폭풍 마지막 고리(slashes.ring) → storm_finish, 낙화 착지 링 스프라이트 두 장 → fall_shockwave, 초승달 바닥 긁힘 → crescent_wave의 균열, 런처 큰 초승달 → launcher_arc_up.
// 남기는 기존 연출(판정과 1:1인 표시 계약, COMBAT-FEEL 9장): 폭풍 범위 고리·회차 호, 돌진 실제 경로 균열·끝 충격, 초승달 본체 스프라이트, 불똥·먼지 입자.
// 색은 영웅 고유색(진홍) + 흰 심 + 잿불 주황 한 갈래만 쓴다(ART-BIBLE: 화면 강조색 1개).
import { PALETTE } from '../../shared/config';
import { KAEL_SKILLS } from '../../shared/skills/kael';
import { SKILLS_BY_HERO } from '../../shared/skills/registry';
import { KAEL_SKILLS_BY_JOB } from '../../shared/skills/kael';
import { LYRA_SKILLS_BY_JOB } from '../../shared/skills/lyra';
import type { SkillTimeline } from '../../shared/skills/types';
import type { CueChannel, SkillHeroId } from '../../shared/skills/types';
import type { FxPreset, ModuleKind } from './types';

const CRIMSON = PALETTE.crimson;
const EMBER = 0xff6a2a;
const WARM_WHITE = 0xfff1d6;
/** 어두운 진홍(링 안쪽 잔물결 바탕: 가산 혼합에서 바닥을 하얗게 띄우지 않는다) */
const DEEP = 0x9a0c1c;

export interface CueLayer {
  readonly module: ModuleKind;
  /** 큐 순간으로부터 늦춰 띄우는 시간(초) */
  readonly delay: number;
  /** 바꿀 값만. y = 영웅 기준 높이 오프셋, yaw = 방향 오프셋, ahead = 앞으로 옮기는 거리 */
  readonly preset: FxPreset;
}

export interface CueEntry {
  readonly cue: string;
  /** 이 큐가 나오는 스킬 번호(Kael 기준) */
  readonly skillId: number;
  /** true면 게임이 이 모듈 조합으로 그린다 */
  readonly live: boolean;
  readonly layers: readonly CueLayer[];
  /**
   * 스킬이 끊기면(회피·피격·장면) 그 자리에서 지울 모듈(COMBAT-FEEL 9.2 "취소되면 그 틱에 지운다").
   * 이 모듈은 이 스킬만 써야 한다(모듈 전체를 비우므로, 테스트가 고정).
   */
  readonly cutOnCancel?: readonly ModuleKind[];
  /**
   * 같은 스킬 안에서 같은 이름의 큐가 여러 번 나올 때(공중 난무 3번·대지 가르기 균열 3칸), n번째(0부터) 큐마다 앞으로/방향을 옮기는 양.
   * 큐 이름만으로는 몇 번째인지 알 수 없어 onSkillCue가 타임라인 칸 번호에서 센다(C9).
   */
  readonly step?: { readonly ahead?: number; readonly yaw?: number };
  readonly note: string;
}

/** 세로로 올려 베는 호의 공통 값(아크와 트레일이 같은 평면을 쓴다) */
const UP_SLASH = { y: 0.9, ahead: 0.2, roll: 0.7, start: -1.1, sweep: 2.2 } as const;
/**
 * 돌진 속도선: 그 틱의 영웅 위치에서 뒤(yaw + π)로 뻗는 직선 트레일. 벽에 막혀도 이미 지나온 길에만 그려진다.
 * c013: variant 2(폭을 카메라 쪽으로 펼침). c012의 roll 0.55 평면은 오른쪽·왼쪽 돌진에서 전투 카메라(약 40° 내려봄)와 거의 나란해
 * 리본이 옆으로 누운 선(폭 거의 0)으로 보였다 — 밝기를 올려도 약했던 원인. 가늘고 긴 줄 세 개(허리·가슴·무릎)를 짧게(0.2초) 쏜다.
 */
const DASH_LINE = { variant: 2, yaw: Math.PI, roll: 0, color: CRIMSON, color2: WARM_WHITE } as const;

export const CUE_MAP: readonly CueEntry[] = [
  {
    cue: 'dash_kick', skillId: 1, live: true,
    note: '피의 돌진 출발: 발밑 발차기 고리 + 몸 높이 속도선 한 줄. 경로 균열·끝 충격은 기존 DashCrack(실제 이동만큼)이 그린다.',
    layers: [
      { module: 'ring', delay: 0, preset: { length: 1.5, width: 0.32, life: 0.28, from: 0.25, color: EMBER, color2: WARM_WHITE, intensity: 1.3, seed: 0.2 } },
      { module: 'trail', delay: 0, preset: { ...DASH_LINE, y: 0.95, length: 2.4, width: 0.26, life: 0.2, tail: 0.14, intensity: 2.2, seed: 0.1 } },
    ],
  },
  {
    cue: 'dash_streak', skillId: 1, live: true,
    note: '피의 돌진 6·12·18틱: 몸 뒤로 뻗는 카메라 쪽 속도선 세 줄(가슴·허리·무릎 높이, 길이 4.4/3.8/3.0m, 0.2초). 끊기면(캔슬) 다음 줄이 나오지 않는다.',
    layers: [
      // c013: 리본이 카메라를 향하게(variant 2) 바꾸고, 가늘고 길게(폭 0.15~0.2) — 굵은 띠가 아니라 날카로운 선으로 읽히게. 같은 틱 세 줄은 높이·각도를 엇갈린다.
      // 트레일 상한 8: 큐 간격 6틱·수명 12틱이라 동시에 많아야 출발 1 + 3 + 3 = 7
      { module: 'trail', delay: 0, preset: { ...DASH_LINE, y: 1.3, yaw: Math.PI + 0.035, length: 4.4, width: 0.18, life: 0.2, tail: 0.15, intensity: 2.4, seed: 0.35 } },
      { module: 'trail', delay: 0, preset: { ...DASH_LINE, y: 0.85, yaw: Math.PI - 0.03, length: 3.8, width: 0.2, life: 0.2, tail: 0.15, intensity: 2.2, seed: 0.7 } },
      { module: 'trail', delay: 0.02, preset: { ...DASH_LINE, y: 0.42, yaw: Math.PI + 0.012, length: 3.0, width: 0.15, life: 0.18, tail: 0.13, intensity: 1.9, seed: 0.55 } },
    ],
  },
  {
    cue: 'storm_range', skillId: 2, live: true,
    note: '칼날 폭풍 시전: 몸 둘레 3m 흡입 소용돌이(0.85초, 9m 흡입을 보이게) + 5m까지 얇게 퍼지는 파동. 범위 고리·회차 호는 기존 StormRange·StormArcs.',
    cutOnCancel: ['vortex'],
    layers: [
      // c013: 몸통은 셰이더에서 옅게(바닥 덮임 계약), 진홍 가장자리 선 + 흰 심 줄무늬(color2)로 읽히게
      { module: 'vortex', delay: 0, preset: { length: 3.0, from: 0.28, width: 0.3, sweep: -7, life: 0.85, color: CRIMSON, color2: WARM_WHITE, intensity: 1.7, seed: 0.4 } },
      { module: 'ring', delay: 0, preset: { length: 5, width: 0.16, life: 0.32, from: 0.45, color: CRIMSON, color2: WARM_WHITE, intensity: 1.1 } },
    ],
  },
  {
    cue: 'storm_finish', skillId: 2, live: true,
    note: '칼날 폭풍 마지막 판정(45틱): 5m까지 퍼지는 얇은 고리 + 안쪽 진홍 고리(기존 slashes.ring 대체, COMBAT-FEEL 9.2 "얇은 고리 1개" 계약 안: 두 번째는 0.05초 뒤 3m 안쪽 잔물결).',
    layers: [
      { module: 'ring', delay: 0, preset: { length: 5, width: 0.24, life: 0.38, from: 0.35, color: CRIMSON, color2: WARM_WHITE, intensity: 1.4, seed: 0.15 } },
      { module: 'ring', delay: 0.05, preset: { length: 3, width: 0.18, life: 0.28, from: 0.3, color: DEEP, color2: CRIMSON, intensity: 1.0, seed: 0.55 } },
    ],
  },
  {
    cue: 'fall_leap', skillId: 3, live: true,
    note: '붉은 낙화 도약: 발밑 흙먼지 고리 + 가는 진홍 기둥(솟는 궤적). 착지 룬은 기존 SkillCast 연출.',
    layers: [
      { module: 'ring', delay: 0, preset: { length: 2.1, width: 0.4, life: 0.32, from: 0.2, color: EMBER, color2: WARM_WHITE, intensity: 1.1, seed: 0.8 } },
      { module: 'pillar', delay: 0, preset: { length: 2.8, width: 0.28, life: 0.3, color: CRIMSON, color2: WARM_WHITE, intensity: 1.2, seed: 0.3 } },
    ],
  },
  {
    cue: 'fall_shockwave', skillId: 3, live: true,
    note: '붉은 낙화 착지: 겹 링 둘 + 방사 균열 + 귀퉁이 비네트. 기존 착지 링 스프라이트 두 장을 대신한다(내리찍는 초승달·불똥은 그대로, 흙먼지는 CombatFx가 충격파 앞 작은 먼지 6장으로 한 번만).',
    layers: [
      { module: 'ring', delay: 0, preset: { length: 4.5, width: 0.6, life: 0.45, from: 0.25, color: EMBER, color2: WARM_WHITE, intensity: 1.8 } },
      { module: 'ring', delay: 0.06, preset: { length: 3, width: 0.4, life: 0.35, from: 0.3, color: CRIMSON, color2: WARM_WHITE, intensity: 1.4 } },
      { module: 'crack', delay: 0, preset: { variant: 1, length: 3.4, life: 1.1, color: CRIMSON, color2: EMBER, intensity: 1.5, seed: 0.6 } },
      // c013: 흙연기 플립북(blast, 영웅 위 지름 3.2m)을 뺐다 — 기존 흙먼지와 겹쳐 영웅을 덮는 회색 덩어리였다. 먼지는 CombatFx가 충격파 앞 작은 먼지로 한 번만 낸다.
      // 비네트는 귀퉁이 모양(screen.ts)으로 좁히고 세기 0.2 → 0.15
      { module: 'screen', delay: 0, preset: { variant: 0, life: 0.3, color: CRIMSON, intensity: 0.15 } },
    ],
  },
  {
    cue: 'crescent_charge', skillId: 4, live: true,
    note: '진홍 초승달 선딜(0.15초): 발밑으로 모여드는 진홍 고리(바깥 1.8m → 0.7m).',
    layers: [
      { module: 'ring', delay: 0, preset: { length: 0.7, from: 2.6, width: 0.22, life: 0.16, color: CRIMSON, color2: WARM_WHITE, intensity: 1.4, seed: 0.9 } },
    ],
  },
  {
    cue: 'crescent_wave', skillId: 4, live: true,
    note: '진홍 초승달 발사(9틱): 초승달 본체(treeFx 스프라이트)와 같은 속도로 8m를 나는 에너지 심(투사체) + 발사 순간 호 + 지나간 길의 균열(기존 바닥 긁힘 대체).',
    layers: [
      { module: 'projectile', delay: 0, preset: { y: 1.0, ahead: 0.8, length: 8, width: 0.9, tail: 2.6, life: 0.42, color: CRIMSON, color2: WARM_WHITE, intensity: 1.7 } },
      { module: 'arc', delay: 0, preset: { y: 1.0, ahead: 0.6, roll: 0.35, length: 1.7, width: 0.8, start: -0.9, sweep: 1.8, life: 0.22, color: CRIMSON, color2: WARM_WHITE, intensity: 1.7 } },
      { module: 'crack', delay: 0.03, preset: { variant: 0, ahead: 0.6, length: 8, width: 1.1, life: 0.9, color: CRIMSON, color2: EMBER, intensity: 1.2, seed: 0.15 } },
    ],
  },
  {
    cue: 'launcher_charge', skillId: 5, live: true,
    note: '천공 올려베기 선딜(0.15초): 앞쪽 발밑으로 모여드는 잿불 고리.',
    layers: [
      { module: 'ring', delay: 0, preset: { ahead: 0.9, length: 0.6, from: 2.8, width: 0.2, life: 0.15, color: EMBER, color2: WARM_WHITE, intensity: 1.3, seed: 0.65 } },
    ],
  },
  {
    cue: 'launcher_arc_up', skillId: 5, live: true,
    note: '천공 올려베기(Lv5 런처) 판정: 기울인 세로 호 + 끝점 트레일 + 앞쪽에서 솟는 가는 기둥(띄움 높이 3m) + 발밑 링 + 앞쪽 바닥 균열 + 짧은 화면 펄스.',
    layers: [
      { module: 'arc', delay: 0, preset: { ...UP_SLASH, length: 2.6, width: 0.95, life: 0.34, color: CRIMSON, color2: WARM_WHITE, intensity: 1.9, seed: 0.2 } },
      { module: 'trail', delay: 0, preset: { ...UP_SLASH, variant: 1, length: 2.72, width: 0.32, life: 0.3, tail: 0.1, color: CRIMSON, color2: WARM_WHITE, intensity: 2.2, seed: 0.5 } },
      // c013: 호와 기둥이 같은 진홍·같은 순간이라 세로 붉은 띠 하나로 붙어 보였다 → 기둥은 호가 거의 그려진 뒤(0.1초) 호 꼭짓점(앞 약 2.8m) 바깥(앞 3.1m, 판정 3m 끝)에서
      // 가늘고(0.15m) 호보다 낮은 값의 잿불 주황으로 솟는다. 순서(진홍 베기 → 주황 띄움 기둥)·색·밝기로 갈라 읽힌다
      { module: 'pillar', delay: 0.1, preset: { ahead: 3.1, length: 2.8, width: 0.15, life: 0.3, color: EMBER, color2: WARM_WHITE, intensity: 1.1, seed: 0.7 } },
      { module: 'ring', delay: 0, preset: { length: 2.1, width: 0.34, life: 0.3, from: 0.35, color: EMBER, color2: WARM_WHITE, intensity: 1.3 } },
      { module: 'crack', delay: 0.03, preset: { variant: 0, ahead: 0.5, length: 2.6, width: 1.8, life: 0.75, color: CRIMSON, color2: EMBER, intensity: 1.3, seed: 0.45 } },
      { module: 'screen', delay: 0, preset: { variant: 0, life: 0.22, color: CRIMSON, intensity: 0.16 } },
    ],
  },
  // ── C9 새 스킬(Lv15·20·25): 같은 진홍(#e0313c)·금(잿불 주황/따뜻한 흰) 계열, 기존 모듈만 재사용 ──
  {
    cue: 'air_rise', skillId: 6, live: true,
    note: '공중 난무 도약(0틱): 발밑 잿불 고리 + 가는 진홍 기둥(위로 솟는 궤적). 낙화 도약보다 가늘고 높다.',
    layers: [
      { module: 'ring', delay: 0, preset: { length: 2.4, width: 0.4, life: 0.3, from: 0.2, color: EMBER, color2: WARM_WHITE, intensity: 1.2, seed: 0.4 } },
      { module: 'pillar', delay: 0, preset: { length: 3.2, width: 0.24, life: 0.34, color: CRIMSON, color2: WARM_WHITE, intensity: 1.3, seed: 0.85 } },
    ],
  },
  {
    cue: 'air_slash', skillId: 6, live: true, step: { yaw: 1.15 },
    note: '공중 난무 3번(8·20·32틱, 판정 5타 중 1·3·5타): 몸 높이에서 220° 가로 호 + 같은 평면 트레일. 번째마다 호 방향이 1.15rad씩 돌아 겹치지 않는다.',
    layers: [
      { module: 'arc', delay: 0, preset: { y: 1.0, roll: 0.42, length: 3.2, width: 0.9, start: -1.9, sweep: 3.8, life: 0.22, color: CRIMSON, color2: WARM_WHITE, intensity: 1.8, seed: 0.3 } },
      { module: 'trail', delay: 0, preset: { y: 1.0, roll: 0.42, variant: 1, length: 3.3, width: 0.3, start: -1.9, sweep: 3.8, life: 0.22, tail: 0.1, color: CRIMSON, color2: WARM_WHITE, intensity: 2.1, seed: 0.6 } },
    ],
  },
  {
    cue: 'air_land', skillId: 6, live: true,
    note: '공중 난무 착지 마무리(47틱, 반경 3.8m): 겹 링 둘 + 방사 균열 + 귀퉁이 비네트(낙화 착지보다 한 단계 작게).',
    layers: [
      { module: 'ring', delay: 0, preset: { length: 4.2, width: 0.55, life: 0.42, from: 0.25, color: EMBER, color2: WARM_WHITE, intensity: 1.7 } },
      { module: 'ring', delay: 0.06, preset: { length: 3, width: 0.38, life: 0.34, from: 0.3, color: CRIMSON, color2: WARM_WHITE, intensity: 1.4 } },
      { module: 'crack', delay: 0, preset: { variant: 1, length: 3.3, life: 1.0, color: CRIMSON, color2: EMBER, intensity: 1.4, seed: 0.35 } },
      { module: 'screen', delay: 0, preset: { variant: 0, life: 0.26, color: CRIMSON, intensity: 0.15 } },
    ],
  },
  {
    cue: 'split_charge', skillId: 7, live: true,
    note: '대지 가르기 선딜(0~18틱): 앞쪽 발밑으로 모여드는 잿불 고리 + 칼끝 쪽에 가늘게 모이는 빛기둥(칼을 치켜드는 순간).',
    layers: [
      { module: 'ring', delay: 0, preset: { ahead: 1.0, length: 0.7, from: 3.0, width: 0.22, life: 0.3, color: EMBER, color2: WARM_WHITE, intensity: 1.3, seed: 0.45 } },
      { module: 'pillar', delay: 0.06, preset: { ahead: 0.9, length: 2.4, width: 0.12, life: 0.26, color: CRIMSON, color2: WARM_WHITE, intensity: 1.0, seed: 0.2 } },
    ],
  },
  {
    cue: 'split_impact', skillId: 7, live: true,
    note: '대지 가르기 내려찍기(18틱): 앞쪽 충격 고리 둘 + 전방 0~10m 긴 균열(데칼이 안에서 바깥으로 자란다) + 솟는 빛기둥 + 귀퉁이 비네트.',
    layers: [
      { module: 'ring', delay: 0, preset: { ahead: 0.3, length: 3.0, width: 0.5, life: 0.4, from: 0.2, color: CRIMSON, color2: WARM_WHITE, intensity: 1.8, seed: 0.1 } },
      { module: 'ring', delay: 0.05, preset: { ahead: 0.3, length: 2.0, width: 0.34, life: 0.3, from: 0.3, color: DEEP, color2: CRIMSON, intensity: 1.1, seed: 0.6 } },
      { module: 'crack', delay: 0, preset: { variant: 0, ahead: 0.2, length: 10, width: 2.6, life: 1.6, color: CRIMSON, color2: EMBER, intensity: 2.6, seed: 0.2 } },
      { module: 'pillar', delay: 0, preset: { ahead: 1.4, length: 3.2, width: 0.32, life: 0.32, color: EMBER, color2: WARM_WHITE, intensity: 1.3, seed: 0.5 } },
      { module: 'screen', delay: 0, preset: { variant: 0, life: 0.24, color: CRIMSON, intensity: 0.15 } },
    ],
  },
  {
    cue: 'split_crack', skillId: 7, live: true, step: { ahead: 2.5 },
    note: '대지 가르기 균열 번짐(25·32·39틱, 70ms 간격): n번째마다 균열이 2.5m 앞으로 번진다(2.5~5, 5~7.5, 7.5~10m) + 구간 한가운데에서 가는 잿불 기둥. 영웅 facing 방향.',
    layers: [
      { module: 'crack', delay: 0, preset: { variant: 0, ahead: 2.5, length: 2.7, width: 2.3, life: 1.4, color: CRIMSON, color2: EMBER, intensity: 2.4, seed: 0.55 } },
      { module: 'pillar', delay: 0, preset: { ahead: 3.75, length: 2.0, width: 0.22, life: 0.28, color: EMBER, color2: WARM_WHITE, intensity: 1.0, seed: 0.3 } },
    ],
  },
  {
    cue: 'spin_aura', skillId: 8, live: true,
    note: '혈검 회전베기 시전(0틱): 발밑 잿불 고리 + 몸을 감싸는 짧은 진홍 오라(슈퍼아머 시작). 오라는 시전 위치 고정이라 0.35초만 남기고, 이후는 spin_ring이 그때 위치에서 다시 감싼다.',
    layers: [
      { module: 'ring', delay: 0, preset: { length: 3.0, width: 0.4, life: 0.3, from: 0.3, color: EMBER, color2: WARM_WHITE, intensity: 1.3, seed: 0.7 } },
      { module: 'aura', delay: 0, preset: { length: 2.2, width: 0.9, life: 0.35, color: CRIMSON, color2: WARM_WHITE, intensity: 1.6, seed: 0.5 } },
    ],
  },
  {
    cue: 'spin_ring', skillId: 8, live: true,
    note: '혈검 회전베기 중간(22·43틱): 몸 주위 붉은 회전 링(허리 높이 360° 호 + 트레일) + 4.2m 바닥 고리 + 짧은 오라. 그 순간의 영웅 위치.',
    layers: [
      { module: 'arc', delay: 0, preset: { y: 0.9, roll: 0.1, length: 3.6, width: 0.85, start: 0, sweep: 6.0, life: 0.3, color: CRIMSON, color2: WARM_WHITE, intensity: 1.7, seed: 0.15 } },
      { module: 'trail', delay: 0, preset: { y: 0.9, roll: 0.1, variant: 1, length: 3.7, width: 0.3, start: 0, sweep: 6.0, life: 0.3, tail: 0.12, color: CRIMSON, color2: WARM_WHITE, intensity: 2.0, seed: 0.4 } },
      { module: 'ring', delay: 0, preset: { length: 4.2, width: 0.45, life: 0.36, from: 0.45, color: CRIMSON, color2: WARM_WHITE, intensity: 1.4, seed: 0.25 } },
      { module: 'aura', delay: 0, preset: { length: 2.2, width: 0.9, life: 0.4, color: CRIMSON, color2: WARM_WHITE, intensity: 1.4, seed: 0.8 } },
    ],
  },
  {
    cue: 'spin_finish', skillId: 8, live: true,
    note: '혈검 회전베기 마무리(58틱): 5m까지 퍼지는 굵은 고리 + 안쪽 진홍 잔물결 + 방사 균열 + 귀퉁이 비네트.',
    layers: [
      { module: 'ring', delay: 0, preset: { length: 5, width: 0.5, life: 0.4, from: 0.35, color: CRIMSON, color2: WARM_WHITE, intensity: 1.7, seed: 0.9 } },
      { module: 'ring', delay: 0.05, preset: { length: 3.4, width: 0.3, life: 0.3, from: 0.3, color: DEEP, color2: CRIMSON, intensity: 1.1, seed: 0.45 } },
      { module: 'crack', delay: 0, preset: { variant: 1, length: 3.6, life: 1.0, color: CRIMSON, color2: EMBER, intensity: 1.3, seed: 0.7 } },
      { module: 'screen', delay: 0, preset: { variant: 0, life: 0.26, color: CRIMSON, intensity: 0.15 } },
    ],
  },
];

/**
 * vfx가 아닌 타임라인 큐(오디오·카메라 전용). 모든 큐가 모듈 조합 또는 이 표 중 한 곳에 반드시 있다(테스트가 고정).
 * 이 큐들은 VFX를 띄우지 않는다: sfx는 오디오가, camera는 카메라 리그가 이름으로 고른다.
 */
export const AV_ONLY_CUES: readonly { readonly name: string; readonly channel: Exclude<CueChannel, 'vfx'>; readonly note: string }[] = [
  { name: 'skill_swing', channel: 'sfx', note: '스킬 휘두름 소리(돌진·폭풍·낙화 도약)' },
  { name: 'skill3_land', channel: 'sfx', note: '붉은 낙화 착지음' },
  { name: 'skill4_wave', channel: 'sfx', note: '진홍 초승달 발사음' },
  { name: 'launcher_windup', channel: 'sfx', note: '천공 올려베기 선딜음' },
  { name: 'launcher_hit', channel: 'sfx', note: '천공 올려베기 타격음' },
  // C9: 아래 이름 중 air_slash·split_impact·spin_finish는 vfx 큐와 같은 이름(채널이 다르다). sfx는 audio/decide.ts가 (스킬 번호, 큐 이름)으로 고른다.
  { name: 'air_slash', channel: 'sfx', note: '공중 난무 베기음(8·32틱, 기존 샘플 피치 변주)' },
  { name: 'split_impact', channel: 'sfx', note: '대지 가르기 내려찍기음(낙화 착지 샘플 + 낮은 피치)' },
  { name: 'spin_start', channel: 'sfx', note: '혈검 회전베기 시전음(폭풍 휘두름 샘플)' },
  { name: 'spin_finish', channel: 'sfx', note: '혈검 회전베기 마무리음(폭풍 마무리 샘플)' },
  { name: 'punch_heavy', channel: 'camera', note: '낙화 착지 카메라 펀치(강)' },
  { name: 'punch_mid', channel: 'camera', note: '런처 판정 카메라 펀치(중)' },
];

/** 큐 이름으로 찾는 색인(생성 때 한 번) */
const BY_NAME = new Map<string, CueEntry>();
for (const e of CUE_MAP) BY_NAME.set(e.cue, e);

export function findCue(name: string): CueEntry | undefined {
  return BY_NAME.get(name);
}

/**
 * 영웅별 스킬 번호 → vfx 큐 이름 배열(타임라인 cues[] 번호 기준, vfx 채널이 아닌 칸은 빈 문자열).
 * c022: 영웅마다 표가 다르다(카엘 표는 예전과 같은 바이트, 라이라 큐는 LyraFx가 그린다)
 */
const NAMES_BY_HERO: Record<string, string[][]> = {};
/** 표 열쇠 → 타임라인 표. 'kael1' = 카엘 전직 갈래 1(소드 마스터, C10-5 — 9·10번만 다르다), 'lyra1' = 라이라 갈래 1(트랩 레인저, C10-6 — 12·13번만 다르다) */
const TABLES: Record<string, readonly SkillTimeline[]> = { ...SKILLS_BY_HERO, kael1: KAEL_SKILLS_BY_JOB[1], lyra1: LYRA_SKILLS_BY_JOB[1] };
const tableKey = (hero: SkillHeroId, job: 0 | 1): string => (job === 1 && (hero === 'kael' || hero === 'lyra') ? hero + '1' : hero);
for (const hero of Object.keys(TABLES)) {
  const rows: string[][] = [];
  for (const sk of TABLES[hero]) {
    const row: string[] = [];
    for (const c of sk.cues) row.push(c.channel === 'vfx' ? c.name : '');
    rows[sk.id] = row;
  }
  NAMES_BY_HERO[hero] = rows;
}

/** 스킬 번호 → cues[] 칸 번호 → 같은 이름이 앞에 몇 번 나왔는지(같은 채널 기준, step 계산용) */
const OCC_BY_HERO: Record<string, number[][]> = {};
for (const hero of Object.keys(TABLES)) {
  const rows: number[][] = [];
  for (const sk of TABLES[hero]) {
    const seen = new Map<string, number>();
    const row: number[] = [];
    for (const c of sk.cues) {
      const key = c.channel + ':' + c.name;
      const n = seen.get(key) ?? 0;
      seen.set(key, n + 1);
      row.push(n);
    }
    rows[sk.id] = row;
  }
  OCC_BY_HERO[hero] = rows;
}

/** 같은 스킬에서 이 칸의 큐가 같은 이름의 몇 번째인지(0부터). 없으면 0 */
export function cueOccurrenceOf(skillId: number, index: number, hero: SkillHeroId = 'kael', job: 0 | 1 = 0): number {
  return (OCC_BY_HERO[tableKey(hero, job)] ?? OCC_BY_HERO.kael)[skillId]?.[index] ?? 0;
}

/** Ev.SkillCue(a = 스킬 번호, c = cues[] 번호)의 vfx 큐 이름. hero = 지금 영웅(생략·모름 = 카엘), job = 전직 갈래(Ev.SkillCue flags & 2, 카엘·라이라). 없으면 빈 문자열 */
export function cueNameOf(skillId: number, index: number, hero: SkillHeroId = 'kael', job: 0 | 1 = 0): string {
  const row = (NAMES_BY_HERO[tableKey(hero, job)] ?? NAMES_BY_HERO.kael)[skillId];
  return row !== undefined && index >= 0 && index < row.length ? row[index] : '';
}

/** 타임라인에 적힌 모든 vfx 큐 이름(매핑 완전성 검사용) */
export function timelineVfxCues(): readonly { skillId: number; name: string }[] {
  const out: { skillId: number; name: string }[] = [];
  for (const sk of KAEL_SKILLS) for (const c of sk.cues) if (c.channel === 'vfx') out.push({ skillId: sk.id, name: c.name });
  return out;
}
